import {
  useEffect, useRef, useState, forwardRef,
  useImperativeHandle, useCallback, useMemo,
} from 'react';
import type { CartItem } from '@/pages/home/page';
import { getWalletBalance, updateWalletAfterOrder, calcApplicableBonus } from '@/hooks/useWallet';
import { saveOrder, generateOTP, saveDeliveryOTP, supabase } from '@/lib/supabase';
import ShareSheet from '@/components/feature/ShareSheet';
import { useFavourites } from '@/hooks/useFavourites';

export interface PortfolioRef {
  filterByCategory: (category: string) => void;
}

interface PortfolioProps {
  onAddToCart: (product: Omit<CartItem, 'quantity'>) => void;
  onOpenCart: () => void;
}

const SAVED_ADDRESS_KEY = 'instant_order_address';
const SAVED_PHONE_KEY   = 'instant_order_phone';

// Empty default - no mock products
const allProducts: {
  id: string | number; name: string; category: string; price: number;
  originalPrice: number; rating: number; reviews: number;
  badge: string; image: string; gender?: string; isBaggy?: boolean;
}[] = [];

const badgeColor = (badge: string) => {
  if (badge === 'HOT')  return 'bg-red-500';
  if (badge === 'SALE') return 'bg-green-500';
  if (badge === 'NEW')  return 'bg-emerald-500';
  return 'bg-gradient-primary';
};

const sizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

type DispProduct = typeof allProducts[0] & { displayId: string; flipped: boolean };
type OrderPhase = 'size-select' | 'address-form' | 'countdown' | 'confirmed';

interface InstantOrder {
  productId: string | number | null; size: string; phone: string; address: string;
  phase: OrderPhase; countdown: number; gpsLoading: boolean;
  walletBalance: number; applyBonus: boolean;
}

const INITIAL_ORDER: InstantOrder = {
  productId: null, size: 'M', phone: '', address: '',
  phase: 'address-form', countdown: 5, gpsLoading: false,
  walletBalance: 0, applyBonus: false,
};

// ── Map raw product (Supabase or localStorage) to Portfolio format ─────────
function mapToPortfolioProduct(p: Record<string, unknown>) {
  // Keep id as string (UUID from Supabase) or number
  const rawId = p.id;
  const id = typeof rawId === 'string' ? rawId : typeof rawId === 'number' ? rawId : String(rawId || '');
  return {
    id,
    name: String(p.name || ''),
    category: String(p.category || ''),
    price: Number(p.price) || 0,
    originalPrice: Number(p.originalPrice || p.original_price) || 0,
    rating: Number(p.rating) || 4.5,
    reviews: Number(p.reviews) || 0,
    badge: String(p.badge || 'NEW'),
    image: String(p.thumbnail || (Array.isArray(p.images) && p.images.length > 0 ? p.images[0] : '')),
    gender: p.gender ? String(p.gender) : undefined,
    isBaggy: Boolean(p.isBaggy),
  };
}

const PORTFOLIO_PAGE_SIZE = 10; // Load 10 products at a time

// ── Load a page of approved products from Supabase ────────────────────────
async function loadApprovedProductsPage(
  offset: number,
  category: string
): Promise<{ products: typeof allProducts; hasMore: boolean }> {
  try {
    let query = supabase
      .from('products')
      .select('id, name, category, price, original_price, thumbnail, images, badge, rating, status')
      .eq('status', 'approved')
      .order('created_at', { ascending: false })
      .range(offset, offset + PORTFOLIO_PAGE_SIZE - 1);

    if (category !== 'All' && category !== 'Top Selling') {
      query = query.eq('category', category);
    } else if (category === 'Top Selling') {
      query = query.in('badge', ['TOP SELLING', 'BESTSELLER', 'HOT']);
    }

    const { data, error } = await query;

    if (!error && data) {
      const mapped = (data as Record<string, unknown>[])
        .map(mapToPortfolioProduct)
        .filter(p => p.name && p.image);
      return { products: mapped, hasMore: data.length === PORTFOLIO_PAGE_SIZE };
    }
  } catch { /* fall through */ }

  // Fallback: localStorage (first page only)
  if (offset === 0) {
    try {
      const raw = localStorage.getItem('listed_products');
      if (!raw) return { products: [], hasMore: false };
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return { products: [], hasMore: false };
      const all = parsed
        .filter((p: Record<string, unknown>) => p.status === 'approved')
        .map(mapToPortfolioProduct)
        .filter(p => p.name && p.image);
      return { products: all, hasMore: false };
    } catch { /* ignore */ }
  }
  return { products: [], hasMore: false };
}

// ── Personalized suggestions helper ───────────────────────────────────────
function getPersonalizedProducts(
  pool: typeof allProducts
): { products: typeof allProducts; interestCategory: string } | null {
  try {
    const raw = localStorage.getItem('currentOrder');
    if (!raw) return null;
    const order = JSON.parse(raw);
    const cat = order?.product?.category as string | undefined;
    if (!cat) return null;
    const similar = pool.filter(p => p.category === cat && p.id !== order?.product?.id);
    if (similar.length < 2) return null;
    return { products: similar.slice(0, 6), interestCategory: cat };
  } catch {
    return null;
  }
}

// ── Fullscreen Viewer ──────────────────────────────────────────────────────
interface FullscreenViewerProps {
  products: typeof allProducts;
  startIndex: number;
  isFavourite: (id: string | number) => boolean;
  onToggleWishlist: (id: string | number) => void;
  onAddToCart: (product: typeof allProducts[0]) => void;
  onInstantOrder: (id: string | number) => void;
  onClose: () => void;
}

function FullscreenViewer({
  products, startIndex, isFavourite,
  onToggleWishlist, onAddToCart, onInstantOrder, onClose,
}: FullscreenViewerProps) {
  const fsScrollRef  = useRef<HTMLDivElement>(null);
  const [fsIndex, setFsIndex] = useState(startIndex);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    if (fsScrollRef.current) {
      const w = fsScrollRef.current.clientWidth;
      fsScrollRef.current.scrollLeft = startIndex * w;
    }
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [startIndex]);

  const handleFsScroll = useCallback(() => {
    if (!fsScrollRef.current) return;
    const w = fsScrollRef.current.clientWidth;
    if (w === 0) return;
    setFsIndex(Math.round(fsScrollRef.current.scrollLeft / w));
  }, []);

  const goFs = useCallback((raw: number) => {
    if (!fsScrollRef.current || products.length === 0) return;
    const len = products.length;
    const idx = ((raw % len) + len) % len;
    fsScrollRef.current.scrollTo({ left: idx * fsScrollRef.current.clientWidth, behavior: 'smooth' });
    setFsIndex(idx);
  }, [products.length]);

  const current = products[fsIndex] ?? products[0];

  return (
    <div className="fixed inset-0 z-[80] bg-black flex flex-col" style={{ animation: 'fsIn .22s ease' }}>
      <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-4 pt-4 pb-8"
           style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,.75) 0%, transparent 100%)' }}>
        <button onClick={onClose}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-black/50 backdrop-blur-sm text-white hover:bg-black/70 transition-colors cursor-pointer">
          <i className="ri-arrow-left-line text-xl"></i>
        </button>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 rounded-full text-xs font-bold text-white ${badgeColor(current.badge)}`}>
            {current.badge}
          </span>
          <span className="text-white/60 text-xs">{fsIndex + 1} / {products.length}</span>
        </div>
        <button onClick={() => setShareOpen(true)}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-black/50 backdrop-blur-sm text-white hover:bg-black/70 transition-colors cursor-pointer">
          <i className="ri-share-forward-line text-xl"></i>
        </button>
      </div>

      <div ref={fsScrollRef} onScroll={handleFsScroll}
           className="flex-1 flex overflow-x-scroll snap-x snap-mandatory"
           style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {products.map((product) => (
          <div key={product.id} className="snap-start flex-shrink-0 w-full h-full relative bg-white">
            <img src={product.image} alt={product.name}
                 className="w-full h-full object-contain object-top" draggable={false} />
          </div>
        ))}
      </div>

      <div className="absolute right-4 bottom-52 flex flex-col items-center gap-5 z-20">
        <button onClick={() => onToggleWishlist(current.id)} className="flex flex-col items-center gap-1 cursor-pointer">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-black/50 backdrop-blur-md hover:bg-black/70 transition-colors">
            <i className={`text-2xl ${isFavourite(current.id) ? 'ri-heart-fill text-red-400' : 'ri-heart-line text-white'}`}></i>
          </div>
          <span className="text-white/70 text-xs">{current.reviews}</span>
        </button>
        <button onClick={() => setShareOpen(true)} className="flex flex-col items-center gap-1 cursor-pointer">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-black/50 backdrop-blur-md hover:bg-black/70 transition-colors">
            <i className="ri-share-forward-line text-white text-2xl"></i>
          </div>
          <span className="text-white/70 text-xs">Share</span>
        </button>
      </div>

      <div className="absolute bottom-0 left-0 right-0 z-20 px-5 pt-24 pb-6"
           style={{ background: 'linear-gradient(to top, rgba(0,0,0,.92) 0%, rgba(0,0,0,.6) 60%, transparent 100%)' }}>
        <span className="text-xs text-gray-400 uppercase tracking-widest">{current.category}</span>
        <h3 className="text-white font-bold text-2xl mt-0.5 mb-1 leading-tight">{current.name}</h3>
        <div className="flex items-center gap-1.5 mb-3">
          <i className="ri-star-fill text-yellow-400 text-sm"></i>
          <span className="text-white text-sm font-medium">{current.rating}</span>
          <span className="text-gray-400 text-sm">({current.reviews} reviews)</span>
        </div>
        <div className="flex items-center gap-3 mb-4">
          <span className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-primary">₹{current.price}</span>
          <span className="text-sm text-gray-400 line-through">₹{current.originalPrice}</span>
          <span className="text-sm text-green-400 font-semibold">
            {Math.round((1 - current.price / current.originalPrice) * 100)}% off
          </span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => onAddToCart(current)}
                  className="flex-1 px-4 py-3 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-white text-sm font-semibold hover:bg-white/30 transition-all cursor-pointer whitespace-nowrap inline-flex items-center justify-center gap-2">
            <i className="ri-shopping-cart-line"></i>Add to Cart
          </button>
          <button onClick={() => onInstantOrder(current.id)}
                  className="flex-1 px-4 py-3 rounded-full bg-gradient-primary text-white text-sm font-semibold hover:opacity-90 transition-all cursor-pointer whitespace-nowrap inline-flex items-center justify-center gap-2">
            <i className="ri-flashlight-fill"></i>Instant Order
          </button>
        </div>
      </div>

      <button onClick={() => goFs(fsIndex - 1)}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-black/50 backdrop-blur-sm text-white hover:bg-black/70 transition-all cursor-pointer z-20">
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button onClick={() => goFs(fsIndex + 1)}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-black/50 backdrop-blur-sm text-white hover:bg-black/70 transition-all cursor-pointer z-20">
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5 z-20">
        {products.map((_, i) => (
          <button key={i} onClick={() => goFs(i)}
                  className={`rounded-full transition-all cursor-pointer ${i === fsIndex ? 'w-5 h-1.5 bg-white' : 'w-1.5 h-1.5 bg-white/35 hover:bg-white/60'}`} />
        ))}
      </div>

      <ShareSheet isOpen={shareOpen} onClose={() => setShareOpen(false)}
                  productName={current.name} productPrice={current.price}
                  productCategory={current.category} productImage={current.image} />
    </div>
  );
}

// ── Main Portfolio Component ───────────────────────────────────────────────
const Portfolio = forwardRef<PortfolioRef, PortfolioProps>(({ onAddToCart, onOpenCart }, ref) => {
  const sectionRef = useRef<HTMLElement>(null);
  const scrollRef  = useRef<HTMLDivElement>(null);
  const [isVisible,      setIsVisible]      = useState(false);
  const [activeCategory, setActiveCategory] = useState('All');
  const [currentIndex,   setCurrentIndex]   = useState(0);
  const { toggleFavourite: toggleFav, isFavourite } = useFavourites();
  const [order,        setOrder]        = useState<InstantOrder>(INITIAL_ORDER);
  const [fsOpen,       setFsOpen]       = useState(false);
  const [fsStartIndex, setFsStartIndex] = useState(0);
  const [shareProduct, setShareProduct] = useState<typeof allProducts[0] | null>(null);
  const [personalized, setPersonalized] = useState<{ products: typeof allProducts; interestCategory: string } | null>(null);
  const [adminProducts, setAdminProducts] = useState<typeof allProducts>([]);
  const [portfolioOffset, setPortfolioOffset] = useState(0);
  const [portfolioHasMore, setPortfolioHasMore] = useState(true);
  const [portfolioLoadingMore, setPortfolioLoadingMore] = useState(false);
  const isFetchingPortfolioRef = useRef(false);

  // Load first page of approved products
  const loadFirstPage = useCallback(async (cat: string) => {
    isFetchingPortfolioRef.current = true;
    const { products, hasMore } = await loadApprovedProductsPage(0, cat);
    setAdminProducts(products);
    setPortfolioOffset(products.length);
    setPortfolioHasMore(hasMore);
    setPersonalized(getPersonalizedProducts(products));
    isFetchingPortfolioRef.current = false;
  }, []);

  // Load next page and append
  const loadNextPage = useCallback(async (cat: string, offset: number) => {
    if (isFetchingPortfolioRef.current) return;
    isFetchingPortfolioRef.current = true;
    setPortfolioLoadingMore(true);
    const { products: newProducts, hasMore } = await loadApprovedProductsPage(offset, cat);
    if (newProducts.length > 0) {
      setAdminProducts(prev => [...prev, ...newProducts]);
      setPortfolioOffset(offset + newProducts.length);
    }
    setPortfolioHasMore(hasMore);
    setPortfolioLoadingMore(false);
    isFetchingPortfolioRef.current = false;
  }, []);

  useEffect(() => {
    loadFirstPage('All');
    const onStorage = () => loadFirstPage('All');
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [loadFirstPage]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Only use admin products - NO fallback to mock data
  const sourceProducts = adminProducts;

  // Reload when category changes
  useEffect(() => {
    setAdminProducts([]);
    setPortfolioOffset(0);
    setPortfolioHasMore(true);
    isFetchingPortfolioRef.current = false;
    loadFirstPage(activeCategory);
  }, [activeCategory, loadFirstPage]);

  // Dynamic categories from source products (fetched separately for filter pills)
  const dynamicCategories = useMemo(() => {
    const cats = Array.from(new Set(sourceProducts.map(p => p.category).filter(Boolean)));
    return ['All', 'Top Selling', ...cats];
  }, [sourceProducts]);

  // With server-side filtering, sourceProducts IS the filtered list
  const filtered = sourceProducts;

  // Infinite loop display
  const [displayedProducts, setDisplayedProducts] = useState<DispProduct[]>([]);
  const [loopRound, setLoopRound] = useState(1);
  const appendingRef = useRef(false);

  useEffect(() => {
    setDisplayedProducts(filtered.map((p, i) => ({ ...p, displayId: `r0-${i}`, flipped: false })));
    setLoopRound(1);
    setCurrentIndex(0);
    appendingRef.current = false;
    if (scrollRef.current) scrollRef.current.scrollLeft = 0;
  }, [filtered]);

  useEffect(() => {
    if (displayedProducts.length === 0 || filtered.length === 0) return;
    if (currentIndex >= displayedProducts.length - 2 && !appendingRef.current) {
      appendingRef.current = true;
      const isFlipped = loopRound % 2 === 1;
      setDisplayedProducts(prev => [
        ...prev,
        ...filtered.map((p, i) => ({ ...p, displayId: `r${loopRound}-${i}`, flipped: isFlipped }))
      ]);
      setLoopRound(prev => prev + 1);
      setTimeout(() => { appendingRef.current = false; }, 200);
    }
  }, [currentIndex, displayedProducts.length, filtered, loopRound]);

  const isUserScrollingRef = useRef(false);
  const autoScrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const goTo = useCallback((rawIndex: number, instant = false) => {
    if (!scrollRef.current || displayedProducts.length === 0) return;
    const index = rawIndex < 0 ? filtered.length - 1 : rawIndex;
    const w = scrollRef.current.clientWidth;
    if (instant) {
      scrollRef.current.scrollLeft = index * w;
      setCurrentIndex(index);
    } else {
      scrollRef.current.scrollTo({ left: index * w, behavior: 'smooth' });
      setCurrentIndex(index);
    }
  }, [displayedProducts.length, filtered.length]);

  // Keep refs in sync for scroll handler (avoids stale closures)
  const portfolioOffsetRef = useRef(portfolioOffset);
  const portfolioHasMoreRef = useRef(portfolioHasMore);
  const adminProductsLenRef = useRef(adminProducts.length);
  const activeCategoryRef = useRef(activeCategory);
  useEffect(() => { portfolioOffsetRef.current = portfolioOffset; }, [portfolioOffset]);
  useEffect(() => { portfolioHasMoreRef.current = portfolioHasMore; }, [portfolioHasMore]);
  useEffect(() => { adminProductsLenRef.current = adminProducts.length; }, [adminProducts.length]);
  useEffect(() => { activeCategoryRef.current = activeCategory; }, [activeCategory]);

  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return;
    const w = scrollRef.current.clientWidth;
    if (w === 0) return;
    const idx = Math.round(scrollRef.current.scrollLeft / w);
    setCurrentIndex(idx);
    // Mark user is scrolling — pause auto-scroll briefly
    isUserScrollingRef.current = true;
    if (autoScrollTimerRef.current) clearTimeout(autoScrollTimerRef.current);
    autoScrollTimerRef.current = setTimeout(() => {
      isUserScrollingRef.current = false;
    }, 4000);
    // Near-end detection: load more when 3 cards from end
    if (
      portfolioHasMoreRef.current &&
      !isFetchingPortfolioRef.current &&
      idx >= adminProductsLenRef.current - 3
    ) {
      loadNextPage(activeCategoryRef.current, portfolioOffsetRef.current);
    }
  }, [loadNextPage]);

  // ── Auto-scroll every 3 seconds ────────────────────────────────────────
  useEffect(() => {
    if (filtered.length <= 1) return;
    const interval = setInterval(() => {
      if (isUserScrollingRef.current) return;
      setCurrentIndex(prev => {
        const next = prev + 1;
        if (scrollRef.current) {
          const w = scrollRef.current.clientWidth;
          scrollRef.current.scrollTo({ left: next * w, behavior: 'smooth' });
        }
        return next;
      });
    }, 3000);
    return () => clearInterval(interval);
  }, [filtered.length]);

  // Countdown timer
  useEffect(() => {
    if (order.phase !== 'countdown') return;
    if (order.countdown <= 0) {
      if (order.productId != null) {
        const product = sourceProducts.find(p => p.id === order.productId);
        if (product) {
          const bonus = order.applyBonus ? calcApplicableBonus(order.walletBalance, product.price) : 0;
          const paid  = product.price - bonus;
          updateWalletAfterOrder(paid, bonus);
          const orderId = 'ORD-' + Date.now();
          const otp     = generateOTP();
          saveOrder({
            id: orderId,
            customer_name: order.phone,
            customer_phone: order.phone,
            customer_address: order.address,
            customer_city: 'Dehradun',
            customer_pincode: '',
            items: [{ id: product.id, name: product.name, price: product.price,
              originalPrice: product.originalPrice, image: product.image,
              category: product.category, quantity: 1, size: order.size }],
            total_amount: product.price, wallet_discount: bonus,
            final_amount: paid, payment_method: 'COD',
            status: 'pending', order_type: 'instant',
          }).then(() => saveDeliveryOTP(orderId, otp));
          localStorage.setItem('currentOrder', JSON.stringify({
            id: orderId, product, size: order.size,
            address: { fullName: order.phone, phone: order.phone, address: order.address, city: 'Dehradun', pincode: '' },
            orderDate: new Date().toISOString(), status: 'confirmed',
          }));
        }
      }
      setOrder(prev => ({ ...prev, phase: 'confirmed' }));
      return;
    }
    const t = setTimeout(() => setOrder(prev => ({ ...prev, countdown: prev.countdown - 1 })), 1000);
    return () => clearTimeout(t);
  }, [order.phase, order.countdown]);

  const toggleWishlist = (id: string | number) => {
    const product = sourceProducts.find(p => String(p.id) === String(id));
    if (product) {
      toggleFav({ id: product.id, name: product.name, price: product.price,
        originalPrice: product.originalPrice, image: product.image,
        category: product.category, badge: product.badge, sizes: [] });
    }
  };

  const handleAddToCart = (product: typeof allProducts[0]) => {
    onAddToCart({ id: product.id, name: product.name, price: product.price,
      originalPrice: product.originalPrice, image: product.image, category: product.category });
  };

  const openFullscreen = (index: number) => { setFsStartIndex(index); setFsOpen(true); };

  const openInstantOrder = (productId: string | number) => {
    const savedAddress  = localStorage.getItem(SAVED_ADDRESS_KEY) ?? '';
    const savedPhone    = localStorage.getItem(SAVED_PHONE_KEY)   ?? '';
    const walletBalance = getWalletBalance();
    if (savedAddress && savedPhone) {
      setOrder({ ...INITIAL_ORDER, productId, address: savedAddress, phone: savedPhone, phase: 'size-select', walletBalance });
    } else {
      setOrder({ ...INITIAL_ORDER, productId, address: savedAddress, phone: savedPhone, phase: 'address-form', walletBalance });
    }
  };

  const closeInstantOrder = () => setOrder(INITIAL_ORDER);

  const fetchGPS = () => {
    if (!navigator.geolocation) return;
    setOrder(prev => ({ ...prev, gpsLoading: true }));
    navigator.geolocation.getCurrentPosition(
      pos => {
        const addr = `GPS: ${pos.coords.latitude.toFixed(5)}, ${pos.coords.longitude.toFixed(5)} (Dehradun)`;
        setOrder(prev => ({ ...prev, address: addr, gpsLoading: false }));
      },
      () => setOrder(prev => ({ ...prev, gpsLoading: false }))
    );
  };

  const submitAddressForm = () => {
    if (!order.phone || !order.address) return;
    localStorage.setItem(SAVED_ADDRESS_KEY, order.address);
    localStorage.setItem(SAVED_PHONE_KEY, order.phone);
    setOrder(prev => ({ ...prev, phase: 'countdown', countdown: 5 }));
  };

  const cancelCountdown = () => {
    const hasSaved = !!(order.address && order.phone);
    setOrder(prev => ({ ...prev, phase: hasSaved ? 'size-select' : 'address-form', countdown: 5 }));
  };

  useImperativeHandle(ref, () => ({
    filterByCategory: (category: string) => {
      setActiveCategory(category);
      sectionRef.current?.scrollIntoView({ behavior: 'smooth' });
    },
  }));

  const instantProduct = order.productId != null
    ? sourceProducts.find(p => String(p.id) === String(order.productId)) ?? null
    : null;

  const progress   = (5 - order.countdown) / 5;
  const radius     = 28;
  const circ       = 2 * Math.PI * radius;
  const dashOffset = circ * (1 - progress);

  return (
    <section id="portfolio" ref={sectionRef} className="relative py-16 md:py-24 overflow-hidden bg-white">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>

      {/* Fullscreen Viewer */}
      {fsOpen && (
        <FullscreenViewer
          products={filtered} startIndex={fsStartIndex}
          isFavourite={isFavourite} onToggleWishlist={toggleWishlist}
          onAddToCart={handleAddToCart}
          onInstantOrder={(id) => { setFsOpen(false); openInstantOrder(id); }}
          onClose={() => setFsOpen(false)}
        />
      )}

      {/* Share Sheet */}
      <ShareSheet isOpen={shareProduct !== null} onClose={() => setShareProduct(null)}
                  productName={shareProduct?.name ?? ''} productPrice={shareProduct?.price ?? 0}
                  productCategory={shareProduct?.category ?? ''} productImage={shareProduct?.image} />

      {/* Instant Order Modal */}
      {order.productId != null && instantProduct && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={closeInstantOrder}></div>
          <div className="relative w-full max-w-md bg-[#12122a] rounded-3xl overflow-hidden"
               style={{ animation: 'slideUp .28s ease' }}>

            <div className="relative h-44 overflow-hidden">
              <img src={instantProduct.image} alt={instantProduct.name}
                   className="w-full h-full object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#12122a] via-black/30 to-transparent"></div>
              <button onClick={closeInstantOrder}
                      className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/50 flex items-center justify-center text-white cursor-pointer hover:bg-black/70 transition-colors z-10">
                <i className="ri-close-line"></i>
              </button>
              <div className="absolute bottom-3 left-4">
                <p className="text-xs text-gray-400 uppercase tracking-wider">{instantProduct.category}</p>
                <h3 className="text-white font-bold text-lg leading-tight">{instantProduct.name}</h3>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-lg font-bold text-transparent bg-clip-text bg-gradient-primary">₹{instantProduct.price}</span>
                  <span className="text-sm text-gray-400 line-through">₹{instantProduct.originalPrice}</span>
                  <span className="text-xs text-green-400 font-semibold">
                    {Math.round((1 - instantProduct.price / instantProduct.originalPrice) * 100)}% off
                  </span>
                </div>
              </div>
            </div>

            {/* Size Select Phase */}
            {order.phase === 'size-select' && (
              <div className="p-5 space-y-4">
                <div>
                  <p className="text-gray-400 text-xs uppercase tracking-wider mb-2">Select Size</p>
                  <div className="flex gap-2 flex-wrap">
                    {sizes.map(s => (
                      <button key={s} onClick={() => setOrder(prev => ({ ...prev, size: s }))}
                              className={`w-11 h-11 rounded-xl text-sm font-semibold transition-all cursor-pointer ${
                                order.size === s ? 'bg-gradient-primary text-white scale-105' : 'bg-white/10 text-gray-300 hover:bg-white/20'
                              }`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-2">
                  <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">Delivering to</p>
                  <div className="flex items-center gap-2">
                    <i className="ri-phone-line text-primary-pink shrink-0 text-sm"></i>
                    <span className="text-white text-sm">{order.phone}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <i className="ri-map-pin-2-fill text-primary-pink shrink-0 text-sm mt-0.5"></i>
                    <span className="text-white text-sm leading-snug">{order.address}</span>
                  </div>
                  <button onClick={() => setOrder(prev => ({ ...prev, phase: 'address-form' }))}
                          className="flex items-center gap-1 text-xs text-gray-400 hover:text-primary-pink transition-colors cursor-pointer mt-1">
                    <i className="ri-edit-line"></i> Change address
                  </button>
                </div>
                <div className={`rounded-2xl p-3.5 border transition-all ${order.walletBalance > 0 ? 'bg-amber-500/10 border-amber-500/25' : 'bg-white/5 border-white/10'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-amber-500/20 shrink-0">
                        <i className="ri-wallet-3-fill text-amber-400 text-sm"></i>
                      </div>
                      <div>
                        <p className="text-white text-xs font-semibold">Wallet Bonus</p>
                        {order.walletBalance > 0
                          ? <p className="text-amber-400 text-[11px]">₹{order.walletBalance} available</p>
                          : <p className="text-gray-500 text-[11px]">No balance yet</p>}
                      </div>
                    </div>
                    {order.walletBalance > 0 && (
                      <button onClick={() => setOrder(prev => ({ ...prev, applyBonus: !prev.applyBonus }))}
                              className={`px-3 py-1 rounded-full text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                                order.applyBonus ? 'bg-green-500 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'
                              }`}>
                        {order.applyBonus ? <><i className="ri-check-line mr-0.5"></i>Applied</> : 'Apply'}
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/25 rounded-xl px-4 py-3">
                  <i className="ri-flashlight-fill text-amber-400 text-lg shrink-0"></i>
                  <p className="text-amber-300 text-xs">30-min delivery · Cash on Delivery</p>
                </div>
                <button onClick={() => setOrder(prev => ({ ...prev, phase: 'countdown', countdown: 5 }))}
                        className="w-full py-3.5 rounded-full bg-gradient-primary text-white font-bold text-sm hover:opacity-90 transition-all cursor-pointer flex items-center justify-center gap-2">
                  <i className="ri-flashlight-fill"></i>
                  Confirm Order · ₹{instantProduct.price - (order.applyBonus ? calcApplicableBonus(order.walletBalance, instantProduct.price) : 0)}
                </button>
              </div>
            )}

            {/* Address Form Phase */}
            {order.phase === 'address-form' && (
              <div className="p-5 space-y-4">
                <div>
                  <p className="text-gray-400 text-xs uppercase tracking-wider mb-2">Select Size</p>
                  <div className="flex gap-2 flex-wrap">
                    {sizes.map(s => (
                      <button key={s} onClick={() => setOrder(prev => ({ ...prev, size: s }))}
                              className={`w-11 h-11 rounded-xl text-sm font-semibold transition-all cursor-pointer ${
                                order.size === s ? 'bg-gradient-primary text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'
                              }`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-gray-400 text-xs uppercase tracking-wider mb-2">Phone Number</p>
                  <div className="flex items-center gap-2 bg-white/8 border border-white/10 rounded-xl px-4 py-3">
                    <i className="ri-phone-line text-gray-400 shrink-0"></i>
                    <input type="tel" placeholder="+91 00000 00000" value={order.phone}
                           onChange={e => setOrder(prev => ({ ...prev, phone: e.target.value }))}
                           className="bg-transparent text-white text-sm flex-1 outline-none placeholder-gray-500" />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-gray-400 text-xs uppercase tracking-wider">Delivery Address</p>
                    <button onClick={fetchGPS} disabled={order.gpsLoading}
                            className="flex items-center gap-1 text-xs text-primary-pink hover:text-white transition-colors cursor-pointer disabled:opacity-50">
                      {order.gpsLoading ? <><i className="ri-loader-4-line animate-spin"></i> Locating…</> : <><i className="ri-gps-line"></i> Use GPS</>}
                    </button>
                  </div>
                  <div className="flex items-start gap-2 bg-white/8 border border-white/10 rounded-xl px-4 py-3">
                    <i className="ri-map-pin-line text-gray-400 mt-0.5 shrink-0"></i>
                    <textarea rows={2} placeholder="Street, Area, Dehradun…" value={order.address}
                              onChange={e => setOrder(prev => ({ ...prev, address: e.target.value }))}
                              className="bg-transparent text-white text-sm flex-1 outline-none placeholder-gray-500 resize-none" />
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/25 rounded-xl px-4 py-3">
                  <i className="ri-flashlight-fill text-amber-400 text-lg shrink-0"></i>
                  <p className="text-amber-300 text-xs">30-min delivery in Dehradun · Cash on Delivery</p>
                </div>
                <button onClick={submitAddressForm} disabled={!order.phone || !order.address}
                        className="w-full py-3.5 rounded-full bg-gradient-primary text-white font-bold text-sm transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 flex items-center justify-center gap-2">
                  <i className="ri-flashlight-fill"></i>
                  Confirm &amp; Place Order · ₹{instantProduct.price}
                </button>
              </div>
            )}

            {/* Countdown Phase */}
            {order.phase === 'countdown' && (
              <div className="p-6">
                <div className="bg-white/5 border border-white/10 rounded-2xl p-4 mb-5 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-sm">Product</span>
                    <span className="text-white text-sm font-semibold">{instantProduct.name}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-sm">Size</span>
                    <span className="text-white text-sm font-semibold">{order.size}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-sm">Phone</span>
                    <span className="text-white text-sm font-semibold">{order.phone}</span>
                  </div>
                  <div className="flex justify-between items-start">
                    <span className="text-gray-400 text-sm shrink-0">Address</span>
                    <span className="text-white text-sm font-semibold text-right ml-3 leading-snug">{order.address}</span>
                  </div>
                  {order.applyBonus && order.walletBalance > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-amber-300 text-sm">Wallet Bonus</span>
                      <span className="text-amber-300 text-sm font-semibold">- ₹{calcApplicableBonus(order.walletBalance, instantProduct.price)}</span>
                    </div>
                  )}
                  <div className="border-t border-white/10 pt-2 flex justify-between items-center">
                    <span className="text-gray-400 text-sm">Total</span>
                    <span className="text-transparent bg-clip-text bg-gradient-primary font-bold text-lg">
                      ₹{instantProduct.price - (order.applyBonus ? calcApplicableBonus(order.walletBalance, instantProduct.price) : 0)}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col items-center gap-3 mb-5">
                  <p className="text-white font-semibold text-base">Placing your order in…</p>
                  <div className="relative w-20 h-20 flex items-center justify-center">
                    <svg className="absolute inset-0 -rotate-90" width="80" height="80">
                      <circle cx="40" cy="40" r={radius} stroke="rgba(255,255,255,.1)" strokeWidth="5" fill="none" />
                      <circle cx="40" cy="40" r={radius} stroke="url(#grad)" strokeWidth="5" fill="none"
                              strokeDasharray={circ} strokeDashoffset={dashOffset}
                              strokeLinecap="round" style={{ transition: 'stroke-dashoffset 1s linear' }} />
                      <defs>
                        <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#a855f7" />
                          <stop offset="100%" stopColor="#ec4899" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <span className="text-white font-bold text-3xl relative z-10">{order.countdown}</span>
                  </div>
                  <p className="text-gray-400 text-xs">Order auto-confirms when timer reaches 0</p>
                </div>
                <div className="flex gap-3">
                  <button onClick={cancelCountdown}
                          className="flex-1 py-3 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-gray-400 text-sm hover:bg-white/30 transition-all cursor-pointer">
                    Edit Details
                  </button>
                  <button onClick={() => setOrder(prev => ({ ...prev, countdown: 0 }))}
                          className="flex-1 py-3 rounded-full bg-gradient-primary text-white text-sm font-bold hover:opacity-90 transition-all cursor-pointer">
                    Order Now!
                  </button>
                </div>
              </div>
            )}

            {/* Confirmed Phase */}
            {order.phase === 'confirmed' && (
              <div className="p-8 text-center">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                  <i className="ri-check-double-line text-green-400 text-4xl"></i>
                </div>
                <h4 className="text-white font-bold text-xl mb-1">Order Placed!</h4>
                <p className="text-gray-400 text-sm mb-1">
                  <span className="text-white font-semibold">{instantProduct.name}</span> · Size {order.size}
                </p>
                <p className="text-gray-400 text-sm mb-3">{order.address}</p>
                <div className="inline-flex items-center gap-2 bg-amber-500/15 border border-amber-500/30 rounded-full px-4 py-2 mb-3">
                  <i className="ri-flashlight-fill text-amber-400"></i>
                  <span className="text-amber-300 text-sm font-medium">Arriving in ~30 minutes</span>
                </div>
                <div className="flex gap-3 mt-3">
                  <button onClick={closeInstantOrder}
                          className="flex-1 py-3 rounded-full bg-gradient-primary text-white font-semibold text-sm cursor-pointer">
                    Continue Shopping
                  </button>
                  <button onClick={() => { localStorage.removeItem(SAVED_ADDRESS_KEY); localStorage.removeItem(SAVED_PHONE_KEY); closeInstantOrder(); }}
                          className="px-4 py-3 rounded-full border border-white/20 text-gray-400 text-xs hover:text-white cursor-pointer">
                    Clear Address
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-2xl mx-auto">

          {/* Picked For You Section */}
          {personalized && (
            <div className={`mb-10 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-2"
                       style={{ background: 'linear-gradient(135deg,#fff0f3,#fff7ed)', border: '1px solid #fecaca' }}>
                    <i className="ri-sparkling-2-fill text-orange-500 text-xs"></i>
                    <span className="text-orange-600 text-xs font-bold uppercase tracking-wider">Picked For You</span>
                  </div>
                  <h3 className="text-gray-900 font-black text-lg leading-tight">
                    Based on your <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-600">interest</span>
                  </h3>
                  <p className="text-gray-400 text-xs mt-0.5">
                    You recently ordered from <strong className="text-gray-600">{personalized.interestCategory}</strong> — you might love these!
                  </p>
                </div>
              </div>
              <div className="flex gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
                {personalized.products.map(product => (
                  <div key={product.id} className="shrink-0 w-44 bg-white rounded-2xl border border-gray-100 overflow-hidden"
                       style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                    <div className="relative h-52 bg-gray-50">
                      <img src={product.image} alt={product.name}
                           className="w-full h-full object-contain object-top" />
                      <div className="absolute top-2 left-2">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold text-white ${badgeColor(product.badge)}`}>
                          {product.badge}
                        </span>
                      </div>
                      <button onClick={() => toggleWishlist(product.id)}
                              className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/80 flex items-center justify-center cursor-pointer">
                        <i className={`text-sm ${isFavourite(product.id) ? 'ri-heart-fill text-red-500' : 'ri-heart-line text-gray-400'}`}></i>
                      </button>
                    </div>
                    <div className="p-3">
                      <p className="text-gray-900 font-bold text-xs leading-tight truncate">{product.name}</p>
                      <div className="flex items-center gap-1 mt-1">
                        <i className="ri-star-fill text-yellow-400 text-[10px]"></i>
                        <span className="text-gray-600 text-[10px]">{product.rating}</span>
                      </div>
                      <div className="flex items-center gap-1.5 mt-1.5">
                        <span className="text-sm font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-600">₹{product.price}</span>
                        <span className="text-[10px] text-gray-400 line-through">₹{product.originalPrice}</span>
                      </div>
                      <button onClick={() => handleAddToCart(product)}
                              className="mt-2 w-full py-1.5 rounded-xl text-white text-[11px] font-bold flex items-center justify-center gap-1 cursor-pointer whitespace-nowrap"
                              style={{ background: 'linear-gradient(90deg,#1f2937,#374151)' }}>
                        <i className="ri-shopping-bag-line text-[11px]"></i>Add to Cart
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Section Header */}
          <div className={`flex flex-col sm:flex-row sm:items-end justify-between mb-8 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-50 border border-orange-200 mb-3">
                <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse"></span>
                <span className="text-orange-600 text-xs font-bold uppercase tracking-wider">Browse Collection</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-black text-gray-900">
                Browse <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-600">Collection</span>
              </h2>
            </div>
            <button className="mt-3 sm:mt-0 flex items-center gap-1.5 text-orange-600 hover:text-orange-700 font-bold transition-colors cursor-pointer whitespace-nowrap text-sm">
              View All <i className="ri-arrow-right-line"></i>
            </button>
          </div>

          {/* ── Premium Category Filter ── */}
          <div className={`mb-5 transition-all duration-700 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            {/* Scrollable category pills */}
            <div className="relative">
              {/* Fade edges */}
              <div className="absolute left-0 top-0 bottom-0 w-6 z-10 pointer-events-none" style={{ background: 'linear-gradient(to right, #fff, transparent)' }}></div>
              <div className="absolute right-0 top-0 bottom-0 w-6 z-10 pointer-events-none" style={{ background: 'linear-gradient(to left, #fff, transparent)' }}></div>
              <div className="flex gap-2 overflow-x-auto pb-2 px-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                {dynamicCategories.map((cat, idx) => {
                  const isActive = activeCategory === cat;
                  const catIcons: Record<string, string> = {
                    'All': 'ri-apps-2-fill',
                    'Top Selling': 'ri-fire-fill',
                    'Track Pants': 'ri-layout-bottom-2-fill',
                    'T-Shirts': 'ri-t-shirt-fill',
                    'Jeans': 'ri-shirt-fill',
                    'Hoodies': 'ri-cloud-windy-fill',
                    'Shorts': 'ri-scissors-cut-fill',
                  };
                  const icon = catIcons[cat] || 'ri-price-tag-3-fill';
                  return (
                    <button
                      key={cat}
                      onClick={() => setActiveCategory(cat)}
                      style={{
                        animationDelay: `${idx * 60}ms`,
                        background: isActive
                          ? 'linear-gradient(135deg, #FF5C00 0%, #FF1744 50%, #D500F9 100%)'
                          : 'rgba(0,0,0,0.04)',
                        boxShadow: isActive ? '0 4px 18px rgba(255,92,0,0.35), 0 1px 4px rgba(0,0,0,0.10)' : 'none',
                        border: isActive ? 'none' : '1.5px solid rgba(0,0,0,0.08)',
                        transform: isActive ? 'scale(1.07)' : 'scale(1)',
                        transition: 'all 0.28s cubic-bezier(0.34,1.56,0.64,1)',
                      }}
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap cursor-pointer flex-shrink-0 ${
                        isActive ? 'text-white' : 'text-gray-600'
                      }`}
                    >
                      <i className={`${icon} text-sm ${isActive ? 'text-white/90' : 'text-gray-400'}`}></i>
                      {cat}
                      {isActive && (
                        <span className="w-1.5 h-1.5 rounded-full bg-white/70 animate-ping ml-0.5" style={{ animationDuration: '1.5s' }}></span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ── Premium Category Name Banner ── */}
          <div
            key={activeCategory}
            className={`flex items-center justify-between mb-4 transition-all duration-700 delay-150 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
          >
            <div
              className="flex-1 flex items-center gap-3 px-4 py-3 rounded-2xl overflow-hidden relative"
              style={{
                background: 'linear-gradient(120deg, #fff7ed 0%, #fff1f2 50%, #fdf4ff 100%)',
                border: '1.5px solid rgba(255,92,0,0.15)',
                animation: 'catBannerIn 0.38s cubic-bezier(0.34,1.56,0.64,1)',
              }}
            >
              {/* Decorative shimmer */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: 'linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.6) 50%, transparent 70%)',
                  animation: 'shimmer 2.4s infinite',
                  backgroundSize: '200% 100%',
                }}
              ></div>
              {/* Gradient vertical bar */}
              <div
                className="w-1.5 h-8 rounded-full flex-shrink-0"
                style={{ background: 'linear-gradient(to bottom, #FF5C00, #FF1744, #D500F9)' }}
              ></div>
              <div className="flex flex-col">
                <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-orange-400">
                  {activeCategory === 'All' ? 'Showing' : 'Category'}
                </span>
                <span
                  className="font-black text-base leading-tight"
                  style={{
                    background: 'linear-gradient(90deg, #FF5C00, #FF1744)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  {activeCategory === 'All' ? 'All Collections' : activeCategory}
                </span>
              </div>
              {filtered.length > 0 && (
                <span
                  className="ml-auto text-[11px] font-bold px-2.5 py-1 rounded-full text-white flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, #FF5C00, #FF1744)' }}
                >
                  {filtered.length} item{filtered.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <div
              className="ml-3 flex items-center gap-1.5 px-3 py-2 rounded-xl cursor-default flex-shrink-0"
              style={{ background: 'linear-gradient(135deg,#f3f4f6,#e5e7eb)', border: '1.5px solid rgba(0,0,0,0.06)' }}
            >
              <i className="ri-swap-line text-gray-500 text-sm"></i>
              <span className="text-gray-500 text-xs font-semibold">Swipe</span>
            </div>
          </div>

          {/* No products state - IMPROVED */}
          {filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 bg-gray-50 rounded-2xl border border-dashed border-gray-200 mb-6">
              <div className="w-20 h-20 rounded-2xl bg-orange-50 flex items-center justify-center mb-4">
                <i className="ri-store-3-line text-orange-400 text-3xl"></i>
              </div>
              <p className="text-gray-800 font-bold text-lg mb-1">No Products Available</p>
              <p className="text-gray-500 text-sm text-center max-w-xs mb-4">
                {activeCategory === 'All' 
                  ? 'Admin panel se approved products add karo. Abhi koi live product nahi hai.' 
                  : `"${activeCategory}" category mein koi approved product nahi hai.`}
              </p>
              <div className="flex items-center gap-2 text-xs text-gray-400 bg-white px-4 py-2 rounded-full border border-gray-200">
                <i className="ri-information-line"></i>
                <span>Only approved products from admin panel are shown here</span>
              </div>
            </div>
          )}

          {/* Horizontal Reel Carousel */}
          {filtered.length > 0 && (
            <>
              <div className={`relative transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                <div ref={scrollRef} onScroll={handleScroll}
                     className="flex overflow-x-scroll snap-x snap-mandatory rounded-3xl overflow-y-hidden"
                     style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', height: '96vh' }}>
                  {displayedProducts.map((product, idx) => (
                    <div
                      key={product.displayId}
                      className="snap-start flex-shrink-0 w-full flex flex-col rounded-3xl overflow-hidden product-card-3d"
                      style={{
                        height: '96vh',
                        background: 'linear-gradient(145deg, #ffffff 0%, #f8f9ff 100%)',
                        border: '1px solid rgba(255,255,255,0.8)',
                      }}
                    >

                      {/* Image */}
                      <div className="relative flex-shrink-0" style={{ height: '62vh', background: 'linear-gradient(160deg, #fafafa 0%, #f0f0f8 100%)' }}>
                        <button onClick={() => openFullscreen(idx % filtered.length)}
                                className="absolute inset-0 w-full h-full cursor-zoom-in focus:outline-none"
                                aria-label={`View ${product.name} fullscreen`}>
                          <img src={product.image} alt={product.name}
                               className="w-full h-full object-contain object-top transition-transform duration-700 ease-out product-img-zoom"
                               style={product.flipped ? { transform: 'scaleX(-1)' } : undefined} />
                        </button>

                        {/* Glassmorphism top overlay */}
                        <div className="absolute top-0 left-0 right-0 h-16 pointer-events-none"
                             style={{ background: 'linear-gradient(to bottom, rgba(255,255,255,0.15), transparent)', backdropFilter: 'blur(0px)' }}>
                        </div>

                        {/* Badge - glassmorphism style */}
                        <div className="absolute top-3 left-3 pointer-events-none">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold text-white ${badgeColor(product.badge)}`}
                                style={{ backdropFilter: 'blur(8px)', boxShadow: '0 2px 12px rgba(0,0,0,0.2)' }}>
                            {product.badge}
                          </span>
                        </div>

                        {/* Fullscreen hint */}
                        <div className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full pointer-events-none"
                             style={{ background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)' }}>
                          <i className="ri-fullscreen-line text-white text-sm"></i>
                        </div>

                        {/* Floating action buttons - glassmorphism */}
                        <div className="absolute right-3 bottom-3 flex flex-col items-center gap-3 z-10">
                          <button onClick={() => toggleWishlist(product.id)} className="flex flex-col items-center gap-0.5 cursor-pointer group/heart">
                            <div className="w-11 h-11 flex items-center justify-center rounded-full transition-all duration-300 group-hover/heart:scale-110"
                                 style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.2)', boxShadow: '0 4px 16px rgba(0,0,0,0.2)' }}>
                              <i className={`text-lg transition-all ${isFavourite(product.id) ? 'ri-heart-fill text-red-400' : 'ri-heart-line text-white'}`}></i>
                            </div>
                            <span className="text-white/70 text-[10px]">{product.reviews}</span>
                          </button>
                          <button onClick={() => setShareProduct(product)} className="flex flex-col items-center gap-0.5 cursor-pointer group/share">
                            <div className="w-11 h-11 flex items-center justify-center rounded-full transition-all duration-300 group-hover/share:scale-110"
                                 style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.2)', boxShadow: '0 4px 16px rgba(0,0,0,0.2)' }}>
                              <i className="ri-share-forward-line text-white text-lg"></i>
                            </div>
                            <span className="text-white/70 text-[10px]">Share</span>
                          </button>
                        </div>

                        {/* Bottom glass strip — discount badge */}
                        {product.originalPrice > product.price && (
                          <div className="absolute bottom-3 left-3 pointer-events-none">
                            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full"
                                 style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.15)' }}>
                              <i className="ri-price-tag-3-fill text-green-400 text-xs"></i>
                              <span className="text-green-400 text-[11px] font-bold">
                                {Math.round((1 - product.price / product.originalPrice) * 100)}% OFF
                              </span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Info — glassmorphism bottom panel */}
                      <div className="flex-1 flex flex-col justify-between px-4 py-4"
                           style={{ background: 'linear-gradient(to bottom, rgba(255,255,255,0.95), rgba(248,249,255,0.98))', borderTop: '1px solid rgba(200,200,220,0.3)', backdropFilter: 'blur(20px)' }}>
                        <div className="space-y-2">
                          {/* Category + rating row */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full"
                                    style={{ background: 'linear-gradient(135deg, rgba(236,72,153,0.12), rgba(168,85,247,0.12))', color: '#ec4899', border: '1px solid rgba(236,72,153,0.2)' }}>
                                {product.category}
                              </span>
                              {product.gender === 'Men' && (
                                <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold whitespace-nowrap"
                                      style={{ background: 'rgba(245,158,11,0.15)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.25)' }}>
                                  Men&apos;s
                                </span>
                              )}
                              {product.isBaggy && (
                                <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold whitespace-nowrap"
                                      style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.25)' }}>
                                  Baggy
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1 px-2 py-1 rounded-full shrink-0"
                                 style={{ background: 'rgba(234,179,8,0.12)', border: '1px solid rgba(234,179,8,0.2)' }}>
                              <i className="ri-star-fill text-yellow-500 text-xs"></i>
                              <span className="text-gray-800 text-xs font-bold">{product.rating}</span>
                              <span className="text-gray-400 text-[10px]">({product.reviews})</span>
                            </div>
                          </div>

                          {/* Product name */}
                          <h3 className="text-gray-900 font-black text-base leading-tight px-0.5">{product.name}</h3>

                          {/* Price row */}
                          <div className="flex items-center gap-2 px-3 py-2.5 rounded-2xl"
                               style={{ background: 'linear-gradient(135deg, rgba(255,92,0,0.08), rgba(255,23,68,0.06))', border: '1px solid rgba(255,92,0,0.12)' }}>
                            <span className="text-xl font-black text-transparent bg-clip-text bg-gradient-primary">₹{product.price}</span>
                            <span className="text-xs text-gray-400 line-through">₹{product.originalPrice}</span>
                            <span className="ml-auto text-[10px] text-green-600 font-black px-2 py-0.5 rounded-full whitespace-nowrap"
                                  style={{ background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.2)' }}>
                              SAVE ₹{product.originalPrice - product.price}
                            </span>
                          </div>
                        </div>

                        {/* CTA — only View Details */}
                        <div className="mt-3">
                          <button onClick={() => openFullscreen(idx % filtered.length)}
                                  className="w-full py-3 rounded-2xl text-white text-xs font-black hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 cursor-pointer whitespace-nowrap inline-flex items-center justify-center gap-1.5"
                                  style={{ background: 'linear-gradient(135deg, #1a1a2e, #16213e)', boxShadow: '0 4px 14px rgba(0,0,0,0.25)' }}>
                            <i className="ri-eye-line text-sm"></i>View Details
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Loading more card — inside scroll container */}
                {portfolioLoadingMore && (
                  <div className="snap-start flex-shrink-0 w-full flex flex-col items-center justify-center rounded-3xl"
                       style={{ height: '96vh', background: 'linear-gradient(145deg, #ffffff 0%, #f8f9ff 100%)', border: '1px solid rgba(255,255,255,0.8)' }}>
                    <div className="w-14 h-14 rounded-full border-2 border-gray-100 border-t-orange-500 animate-spin mb-4"></div>
                    <p className="text-gray-500 text-sm font-medium">Loading more products...</p>
                    <p className="text-gray-300 text-xs mt-1">Swipe karte raho!</p>
                  </div>
                )}

                {/* Arrows */}
                <button onClick={() => goTo(currentIndex - 1)}
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-black/50 backdrop-blur-sm text-white hover:bg-black/70 transition-all cursor-pointer z-10">
                  <i className="ri-arrow-left-s-line text-xl"></i>
                </button>
                <button onClick={() => goTo(currentIndex + 1)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-black/50 backdrop-blur-sm text-white hover:bg-black/70 transition-all cursor-pointer z-10">
                  <i className="ri-arrow-right-s-line text-xl"></i>
                </button>
              </div>

              {/* Dot Indicators */}
              <div className={`flex justify-center gap-2 mt-5 transition-all duration-700 delay-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
                {filtered.map((_, i) => (
                  <button key={i} onClick={() => goTo(i)}
                          className={`rounded-full transition-all cursor-pointer ${
                            i === (currentIndex % filtered.length) ? 'w-6 h-2 bg-gradient-primary' : 'w-2 h-2 bg-gray-300 hover:bg-gray-500'
                          }`} />
                ))}
              </div>
            </>
          )}

          {/* Trust Badges */}
          <div className={`mt-6 transition-all duration-700 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className="grid grid-cols-3 gap-3">
              <div className="flex flex-col items-center gap-2 rounded-2xl px-3 py-4 border border-amber-100 bg-amber-50">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-amber-100">
                  <i className="ri-money-rupee-circle-fill text-amber-600 text-xl"></i>
                </div>
                <div className="text-center">
                  <p className="text-gray-900 text-xs font-black">Cash on Delivery</p>
                  <p className="text-gray-400 text-[10px] mt-0.5">Pay at door</p>
                </div>
              </div>
              <div className="flex flex-col items-center gap-2 rounded-2xl px-3 py-4 border border-emerald-100 bg-emerald-50">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-emerald-100">
                  <i className="ri-loop-left-fill text-emerald-600 text-xl"></i>
                </div>
                <div className="text-center">
                  <p className="text-gray-900 text-xs font-black">Easy Returns</p>
                  <p className="text-gray-400 text-[10px] mt-0.5">7-day policy</p>
                </div>
              </div>
              <div className="flex flex-col items-center gap-2 rounded-2xl px-3 py-4 border border-yellow-100 bg-yellow-50">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-yellow-100">
                  <i className="ri-star-smile-fill text-yellow-600 text-xl"></i>
                </div>
                <div className="text-center">
                  <p className="text-gray-900 text-xs font-black">4.8★ Rating</p>
                  <p className="text-gray-400 text-[10px] mt-0.5">1200+ reviews</p>
                </div>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-center gap-2 rounded-2xl px-4 py-3"
                 style={{ background: 'linear-gradient(135deg, #fff7ed, #fff1f2)', border: '1px solid #fed7aa' }}>
              <i className="ri-flashlight-fill text-orange-500 text-sm"></i>
              <span className="text-gray-700 text-xs font-semibold">30-min delivery · 100% genuine products · Safe packaging</span>
            </div>
          </div>

        </div>
      </div>

      <style>{`
        @keyframes slideUp { from { transform: translateY(40px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes fsIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes catBannerIn {
          from { opacity: 0; transform: translateX(-18px) scale(0.96); }
          to   { opacity: 1; transform: translateX(0) scale(1); }
        }
        @keyframes shimmer {
          0%   { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes catPillIn {
          from { opacity: 0; transform: translateY(10px) scale(0.88); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes cardFloat {
          0%, 100% { transform: translateY(0px) rotateX(0deg); }
          50% { transform: translateY(-4px) rotateX(1deg); }
        }
        @keyframes glassShine {
          0%   { background-position: -200% center; opacity: 0; }
          30%  { opacity: 0.7; }
          100% { background-position: 200% center; opacity: 0; }
        }
        .product-card-3d {
          transform-style: preserve-3d;
          perspective: 1000px;
          transition: box-shadow 0.4s ease, transform 0.4s cubic-bezier(0.23,1,0.32,1);
          box-shadow: 0 2px 20px rgba(0,0,0,0.06), 0 1px 4px rgba(0,0,0,0.04);
          position: relative;
          overflow: hidden;
        }
        .product-card-3d::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.55) 50%, transparent 70%);
          background-size: 200% 100%;
          pointer-events: none;
          z-index: 5;
          opacity: 0;
          animation: glassShine 4s ease infinite 1s;
          border-radius: inherit;
        }
        .product-card-3d::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: inherit;
          background: linear-gradient(135deg, rgba(255,255,255,0.3) 0%, transparent 50%, rgba(255,255,255,0.1) 100%);
          pointer-events: none;
          z-index: 4;
        }
        .product-img-zoom {
          transition: transform 0.6s cubic-bezier(0.23,1,0.32,1);
        }
        .product-card-3d:hover .product-img-zoom {
          transform: scale(1.04);
        }
      `}</style>
    </section>
  );
});

Portfolio.displayName = 'Portfolio';
export default Portfolio;
